import {
  Dispatch,
  SetStateAction,
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useDragToScroll, useRouter } from '@/hooks';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TablePagination from '@mui/material/TablePagination';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ColumnSelectionDialog } from './assets/ColumnSelectionDialog';
import {
  selectedRiskAssessmentFilters,
  riskAssessmentAssemblyTemplate,
  isTemplateFieldDataUpdated,
  selectedAssemblyId as selectedAssemblyIdState,
  maintainSelectedEquipments,
  selectedAssemblyIds as selectedIds,
  unselectedAssemblyIds,
  riskAssessmentSearchTerm,
  structureFeatures as structureFeaturesState,
} from '@/components/Analysis/state';
import {
  useGetAssembliesByRiskAssessmentFiltersLazyQuery,
  RiskFieldsTypeEnum,
  GetAssembliesByRiskAssessmentFiltersQuery,
  Assembly,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { CustomTableHead } from '../../shared/EquipmentTable/CustomTableHead';
import { CustomTableBody } from '../../shared/EquipmentTable/CustomTableBody';
import { Columns } from '@/types';
import { getEquipmentColumnsFromAssemblyTemplate } from '@/utils';
import { abyssColors } from '@/theme/colors';
import { Stack } from '@mui/material';
import { ReviewOptionsContainer } from '../ReviewOptionsContainer';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

type EquipmentTableProps = {
  selectedTab: string;
  searchTerm: string;
  setUpdateAllAssemblies: Dispatch<SetStateAction<boolean>>;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  setHideContainer: Dispatch<SetStateAction<boolean>>;
  updateAll: boolean;
};

const TableLoader = () => {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundColor: 'rgba(255,255,255,0.6)',
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 10,
      }}
    >
      <img
        src="/assets/abyss_fabric_mini_logo_black.svg"
        style={{ height: '6.4rem' }}
        alt="table-loader"
      />
    </Stack>
  );
};

export const RiskAssessmentEquipmentTable = ({
  selectedTab,
  searchTerm,
  setUpdateAllAssemblies,
  setDrawerOpen,
  updateAll,
  setSearchTerm,
  setHideContainer,
}: EquipmentTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const structureId = useGetStructureId();
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const setSearchState = useSetAtom(riskAssessmentSearchTerm);
  const structureFeatures = useAtomValue(structureFeaturesState);
  const [isAssemblyUpdated, setIsAssemblyUpdated] = useAtom(isTemplateFieldDataUpdated);
  const [maintainSelections, setMaintainSelections] = useAtom(maintainSelectedEquipments);
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(selectedAssemblyIdState);
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useAtom(selectedIds);
  const [excludedAssemblyIds, setExcludedAssemblyIds] = useAtom(unselectedAssemblyIds);
  const [allAssemblies, setAllAssemblies] = useState<
    GetAssembliesByRiskAssessmentFiltersQuery['getAssembliesByRiskAssessmentFilters']
  >([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeColumns, setActiveColumns] = useState<Columns[]>([]);
  const [assembliesCount, setAssembliesCount] = useState(0);
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');

  const router = useRouter();

  const filteredRiskFieldKeys = useMemo(() => {
    if (template?.riskFields) {
      return template.riskFields
        .filter(
          (riskField) =>
            !riskField.isHiddenColumn && riskField.type.includes(selectedTab as RiskFieldsTypeEnum)
        )
        .map((riskField) => riskField.fieldKey);
    }
    return [];
  }, [template?.riskFields, selectedTab]);

  const filteredTemplateFieldKeys = useMemo(() => {
    if (filteredRiskFieldKeys) {
      const filteredKeys = activeColumns?.map((header) => header.templateFieldKey);
      return template?.fields
        .filter(
          (field) =>
            filteredRiskFieldKeys.includes(field.key!) && filteredKeys?.includes(field.key!)
        )
        .map((field) => field.key);
    }
    return [];
  }, [activeColumns, filteredRiskFieldKeys, template?.fields]);

  const [fetchFilteredAssemblies, { loading }] = useGetAssembliesByRiskAssessmentFiltersLazyQuery({
    onCompleted: (data) => {
      const assemblies = data?.getAssembliesByRiskAssessmentFilters;
      if (assemblies) {
        const filteredAssemblyData = assemblies?.map((filteredAssembly) => ({
          ...filteredAssembly,
          templateFieldData: Object.fromEntries(
            Object.entries(filteredAssembly.templateFieldData).filter(([key]) =>
              filteredTemplateFieldKeys?.includes(key)
            )
          ),
        }));
        setAllAssemblies(filteredAssemblyData);
        if (!maintainSelections) {
          setUpdateAllAssemblies(true);
          setSelectedAssemblyIds(filteredAssemblyData.map((assembly) => assembly.id));
          setExcludedAssemblyIds([]);
        }
        setAssembliesCount(data?.getRiskAssembliesCount?.count ?? 0);
        setIsAssemblyUpdated(false);
      }
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { containerReference, handleMouseDown } = useDragToScroll();

  useEffect(() => {
    if (selectedAssemblyId && selectedTab === RiskFieldsTypeEnum.Review) setDrawerOpen(true);
  }, [selectedAssemblyId, setDrawerOpen, selectedTab]);

  const allHeaders = useMemo(() => {
    if (filteredRiskFieldKeys && template) {
      const columnsFromTemplate = getEquipmentColumnsFromAssemblyTemplate(template);
      const headers = columnsFromTemplate.filter((field) =>
        filteredRiskFieldKeys.includes(field.templateFieldKey!)
      );

      if (Object.keys(structureFeatures?.features).includes('viewer')) {
        headers.push({
          isSortable: false,
          visibility: true,
          key: 'view3D',
          columnName: 'View',
          displayConfig: {
            type: '3DBUTTON',
          },
          onCellClick: (event, item) => {
            event.stopPropagation();
            setSelectedAssemblyId(undefined);

            const url = `/analysis/${structureId}/viewer?assembly=${item?.tagName}`;

            const isCtrlClick = event.ctrlKey || event.metaKey;
            if (isCtrlClick) window.open(url, '_blank');
            else router.push(url);
          },
        });
      }
      setActiveColumns(headers);

      return headers;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredRiskFieldKeys,
    template,
    structureFeatures?.features,
    setSelectedAssemblyId,
    structureId,
  ]);

  useEffect(() => {
    (async () => {
      try {
        await fetchFilteredAssemblies({
          variables: {
            input: {
              structureId,
              templateId: template?.id!,
              selectedFilters,
              searchText: searchTerm,
            },
            pageInfo: {
              skip: page * rowsPerPage,
              limit: rowsPerPage,
              sortBy,
              sortDir: sortDirection,
            },
          },
        });
      } catch (error) {
        console.error('Query execution error:', error);
      }
    })();
  }, [
    fetchFilteredAssemblies,
    selectedFilters,
    structureId,
    page,
    rowsPerPage,
    sortBy,
    sortDirection,
    template?.id,
    searchTerm,
    filteredTemplateFieldKeys,
    isAssemblyUpdated,
    setIsAssemblyUpdated,
    maintainSelections,
    setMaintainSelections,
    updateAll,
    setSelectedAssemblyIds,
    setExcludedAssemblyIds,
    setUpdateAllAssemblies,
  ]);

  useEffect(() => {
    setPage(0);
  }, [selectedFilters]);

  useEffect(() => {
    setSearchState(searchTerm);
  }, [searchTerm, setSearchState]);

  const handleChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    },
    [setPage, setRowsPerPage]
  );

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleHeaderCheckBoxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked && allAssemblies?.length) {
        setUpdateAllAssemblies(true);
        setSelectedAssemblyIds(allAssemblies.map((assembly) => assembly.id));
        setExcludedAssemblyIds([]);
      } else {
        setMaintainSelections(true);
        setUpdateAllAssemblies(false);
        setSelectedAssemblyIds([]);
      }
    },
    [
      allAssemblies,
      setExcludedAssemblyIds,
      setMaintainSelections,
      setSelectedAssemblyIds,
      setUpdateAllAssemblies,
    ]
  );

  const handleRowCheckBoxChange = useCallback(
    (assemblyId: string) => {
      if (selectedAssemblyIds.includes(assemblyId)) {
        setSelectedAssemblyIds(
          selectedAssemblyIds.filter((selectedId) => selectedId !== assemblyId)
        );
        setExcludedAssemblyIds([...excludedAssemblyIds, assemblyId]);
      } else {
        setSelectedAssemblyIds([...selectedAssemblyIds, assemblyId]);
        setExcludedAssemblyIds(
          selectedAssemblyIds.filter((selectedId) => selectedId !== assemblyId)
        );
      }
    },
    [excludedAssemblyIds, selectedAssemblyIds, setExcludedAssemblyIds, setSelectedAssemblyIds]
  );

  const handleSort = useCallback(
    (property: string) => {
      const isAsc = sortBy === property && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(property);
    },
    [sortBy, sortDirection]
  );

  const toggleDialog = useCallback(() => {
    setOpenDialog(!openDialog);
  }, [openDialog]);

  const handleColumnSelection = useCallback(
    (ids: (string | undefined)[]) => {
      setActiveColumns(allHeaders?.filter((header) => ids?.includes(header.templateFieldKey)));
    },
    [allHeaders]
  );

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, assembly: Assembly) => {
      if ((event.target as HTMLInputElement).type === 'checkbox') {
        return;
      }
      if (selectedAssemblyId === assembly.id) {
        setSelectedAssemblyId(undefined);
        return;
      }
      setSelectedAssemblyId(assembly.id);
    },
    [selectedAssemblyId, setSelectedAssemblyId]
  );

  return (
    <>
      <ColumnSelectionDialog
        isOpen={openDialog}
        allHeaders={allHeaders}
        onClose={toggleDialog}
        handleColumnSelection={handleColumnSelection}
      />
      {selectedTab === RiskFieldsTypeEnum.Review && (
        <Box sx={{ mt: 3 }}>
          <ReviewOptionsContainer
            loading={loading}
            handleSearch={setSearchTerm}
            handleSetHideContainer={setHideContainer}
          />
        </Box>
      )}
      <TableContainer
        sx={{ position: 'relative', minHeight: 320, display: 'flex', flexDirection: 'column' }}
      >
        {loading && <TableLoader />}
        <Box
          ref={containerReference}
          sx={{ overflowX: 'auto', flexGrow: 1 }}
          onMouseDown={handleMouseDown}
        >
          <Table stickyHeader>
            <CustomTableHead
              orderBy={sortBy}
              order={sortDirection}
              activeColumns={activeColumns}
              toggleDialog={toggleDialog}
              handleHeaderCheckboxChange={handleHeaderCheckBoxChange}
              handleSort={handleSort}
              isChecked={updateAll}
            />
            {allAssemblies.length > 0 && (
              <CustomTableBody<Assembly>
                data={allAssemblies as Assembly[]}
                activeColumns={activeColumns}
                onCheckboxChange={handleRowCheckBoxChange}
                selectAll={updateAll}
                selectedIds={selectedAssemblyIds}
                unselectedIds={excludedAssemblyIds}
                onRowClick={handleRowClick}
                selectedRow={selectedAssemblyId}
              />
            )}
          </Table>
        </Box>
        {allAssemblies.length > 0 && (
          <Box
            sx={{
              alignSelf: 'flex-end',
              mt: 1,
              pb: 2,
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <TablePagination
              count={assembliesCount}
              page={page}
              rowsPerPageOptions={[
                { label: '10', value: 10 },
                { label: '25', value: 25 },
                { label: '100', value: 100 },
                { label: 'All', value: assembliesCount },
              ]}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onPageChange={handleChangePage}
            />
          </Box>
        )}
        {!loading && allAssemblies.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              borderRadius: '8px',
              minHeight: 320,
            }}
          >
            <InfoOutlinedIcon sx={{ color: abyssColors.primary[400], fontSize: '6rem' }} />
            <Typography
              variant="h6"
              fontWeight={500}
              sx={{
                color: abyssColors.primary[400],
                whiteSpace: 'nowrap',
                mt: 1,
              }}
            >
              No data found
            </Typography>
          </Box>
        )}
      </TableContainer>
    </>
  );
};
